import React from 'react';
import { Phone, MapPin, Clock, Instagram, Calendar, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { barbershopInfo } from '../data/mock';

const ContactSection = () => {
  return (
    <section id="contact" className="py-20 sm:py-32 bg-slate-900 relative">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#c9a962]/5 via-transparent to-[#c9a962]/5" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" dir="rtl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Contact Info */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-px w-12 bg-[#c9a962]" />
              <span className="text-[#c9a962] text-sm tracking-wider">צור קשר</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              בואו <span className="text-[#c9a962]">לבקר</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10">
              מחכים לכם ב-NY Barbershop. צרו קשר וקבעו תור עכשיו.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Phone */}
              <a
                href={`tel:${barbershopInfo.phone}`}
                className="flex items-center gap-4 p-4 bg-slate-950 border border-slate-800 hover:border-[#c9a962]/50 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-[#c9a962]/10 flex items-center justify-center group-hover:bg-[#c9a962]/20 transition-colors">
                  <Phone className="w-6 h-6 text-[#c9a962]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">טלפון</p>
                  <p className="text-white font-semibold text-lg">{barbershopInfo.phone}</p>
                </div>
              </a>

              {/* Address */}
              <a
                href={barbershopInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-slate-950 border border-slate-800 hover:border-[#c9a962]/50 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-[#c9a962]/10 flex items-center justify-center group-hover:bg-[#c9a962]/20 transition-colors">
                  <MapPin className="w-6 h-6 text-[#c9a962]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">כתובת</p>
                  <p className="text-white font-semibold text-lg">{barbershopInfo.address}</p>
                </div>
              </a>

              {/* Hours */}
              <div className="flex items-center gap-4 p-4 bg-slate-950 border border-slate-800">
                <div className="w-14 h-14 bg-[#c9a962]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#c9a962]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">שעות פעילות</p>
                  <p className="text-white font-semibold text-lg">{barbershopInfo.hours}</p>
                  <p className="text-[#c9a962] text-sm">{barbershopInfo.hoursNote}</p>
                </div>
              </div>

              {/* Instagram */}
              <a
                href={barbershopInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-slate-950 border border-slate-800 hover:border-[#c9a962]/50 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-[#c9a962]/10 flex items-center justify-center group-hover:bg-[#c9a962]/20 transition-colors">
                  <Instagram className="w-6 h-6 text-[#c9a962]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">אינסטגרם</p>
                  <p className="text-white font-semibold text-lg">@{barbershopInfo.instagram}</p>
                </div>
              </a>
            </div>
          </div>

          {/* CTA Card */}
          <div className="relative">
            <div className="bg-slate-950 border border-[#c9a962]/30 p-8 sm:p-12">
              {/* Logo */}
              <div className="text-center mb-8">
                <div className="inline-block">
                  <div className="w-20 h-20 border-2 border-[#c9a962] flex items-center justify-center mx-auto mb-4">
                    <span className="text-[#c9a962] font-bold text-2xl">NY</span>
                  </div>
                  <p className="text-[#c9a962] text-xs tracking-[0.3em] uppercase">Barbershop</p>
                </div>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4">
                מוכנים לתור הבא?
              </h3>
              <p className="text-slate-400 text-center mb-8">
                הזמינו תור בלחיצת כפתור ותיהנו מחווית תספורת מקצועית
              </p>

              <div className="space-y-4">
                <a
                  href={barbershopInfo.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-[#c9a962] hover:bg-[#b8962f] text-slate-950 font-bold text-lg py-6 transition-all duration-300 hover:shadow-xl hover:shadow-[#c9a962]/30">
                    <Calendar className="w-5 h-5 ml-2" />
                    הזמן תור אונליין
                  </Button>
                </a>

                <a
                  href={barbershopInfo.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#c9a962]/50 text-[#c9a962] hover:bg-[#c9a962]/10 font-semibold text-lg py-6 transition-all duration-300"
                  >
                    <Navigation className="w-5 h-5 ml-2" />
                    נווט למספרה
                  </Button>
                </a>
              </div>

              {/* Decorative Corner */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#c9a962]" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#c9a962]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
